import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import i18next from 'i18next'
import { I18nextProvider, initReactI18next } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import de from './locales/de.json'
import en from './locales/en.json'
import es from './locales/es.json'
import it from './locales/it.json'
import pt from './locales/pt.json'
import tr from './locales/tr.json'

export const STORAGE_KEY = 'gbsoft_lang'
export const MARKETING_LANGS = ['tr', 'en', 'es', 'de', 'it', 'pt'] as const
export type MarketingLang = (typeof MARKETING_LANGS)[number]

type MarketingLangContextValue = {
  lang: MarketingLang
  setLang: (next: MarketingLang) => void
}

const MarketingLangContext = createContext<MarketingLangContextValue | null>(null)

function isMarketingLang(value: string | null): value is MarketingLang {
  return value !== null && (MARKETING_LANGS as readonly string[]).includes(value)
}

function ogLocale(lang: MarketingLang): string {
  switch (lang) {
    case 'en':
      return 'en_US'
    case 'es':
      return 'es_ES'
    case 'de':
      return 'de_DE'
    case 'it':
      return 'it_IT'
    case 'pt':
      return 'pt_PT'
    case 'tr':
      return 'tr_TR'
    default: {
      const _exhaustive: never = lang
      return _exhaustive
    }
  }
}

export function readStoredLang(): MarketingLang {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (isMarketingLang(value)) return value
  } catch {
    // private mode / blocked storage
  }
  return 'tr'
}

function metaTitleKey(pathname: string): string {
  if (pathname.startsWith('/city-vision')) return 'meta.cityVisionTitle'
  if (pathname.startsWith('/privacy-policy')) return 'meta.privacyTitle'
  return 'meta.title'
}

function setMetaContent(selector: string, content: string) {
  const el = document.querySelector(selector)
  if (el) el.setAttribute('content', content)
}

export const marketingI18n = i18next.createInstance()

void marketingI18n.use(initReactI18next).init({
  resources: {
    tr: { translation: tr },
    en: { translation: en },
    es: { translation: es },
    de: { translation: de },
    it: { translation: it },
    pt: { translation: pt },
  },
  lng: typeof window !== 'undefined' ? readStoredLang() : 'tr',
  fallbackLng: 'tr',
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
})

export function MarketingLocaleProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [lang, setLangState] = useState<MarketingLang>(() =>
    typeof window !== 'undefined' ? readStoredLang() : 'tr',
  )

  useEffect(() => {
    void marketingI18n.changeLanguage(lang).then(() => {
      document.documentElement.lang = lang
      setMetaContent('meta[property="og:locale"]', ogLocale(lang))
      document.title = marketingI18n.t(metaTitleKey(location.pathname))
      setMetaContent('meta[name="description"]', marketingI18n.t('meta.description'))
      setMetaContent('meta[property="og:title"]', marketingI18n.t('meta.ogTitle'))
      setMetaContent('meta[property="og:description"]', marketingI18n.t('meta.ogDescription'))
      setMetaContent('meta[name="twitter:title"]', marketingI18n.t('meta.ogTitle'))
      setMetaContent('meta[name="twitter:description"]', marketingI18n.t('meta.ogDescription'))
    })
  }, [lang, location.pathname])

  useEffect(() => {
    return () => {
      document.documentElement.lang = 'tr'
    }
  }, [])

  const setLang = useCallback((next: MarketingLang) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignore
    }
    setLangState(next)
  }, [])

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang])

  return (
    <I18nextProvider i18n={marketingI18n}>
      <MarketingLangContext.Provider value={value}>{children}</MarketingLangContext.Provider>
    </I18nextProvider>
  )
}

export function useMarketingLang(): MarketingLangContextValue {
  const ctx = useContext(MarketingLangContext)
  if (!ctx) {
    throw new Error('useMarketingLang must be used within MarketingLocaleProvider')
  }
  return ctx
}
